import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xhubio-saas/core-frontend/ui-components'
import type { ReactNode } from 'react'
import type { BillingTab } from './billingState'

export interface BillingTabPanel {
  id: BillingTab
  label: string
  content: ReactNode
}

/** Fixed classes per tab count — Tailwind only sees spelled-out classes. */
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
}

export interface BillingTabsProps {
  panels: BillingTabPanel[]
  value: BillingTab
  onValueChange: (tab: BillingTab) => void
  label: string
}

/**
 * The three sections of the billing page as tabs — segmented control after `.claude/UI_GUIDELINES.md` §27, same
 * trigger build as the validation `Workbench`.
 *
 * ⚪ Not the `Workbench` itself: it keeps its tab in `sessionStorage` only, here the tab is part of the address
 * (`?tab=`, plan E5) so `/subscription` and `/usage` can land in their section. The page owns the value.
 *
 * Without `keepMounted`: the hidden panels hold no state worth keeping (the pricing cards and the chart render from
 * data the page already has), and the usage chart measures its width on mount — hidden it would measure zero.
 */
export function BillingTabs({ panels, value, onValueChange, label }: BillingTabsProps) {
  return (
    <section aria-label={label} data-testid="billing-tabs">
      <Tabs value={value} onValueChange={(next) => onValueChange(String(next) as BillingTab)}>
        <TabsList className={`grid w-full ${GRID_COLS[panels.length] ?? 'grid-cols-3'}`}>
          {panels.map((p) => (
            <TabsTrigger
              key={p.id}
              value={p.id}
              // 🔴 Own focus ring — the primitive's ring did not reach the browser (measured 2026-10-10 on the
              // validation workbench). Outline instead of shadow; the transition covers only background and text.
              className="min-w-0 transition-[background-color,color] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring active:bg-muted data-active:active:bg-primary/90"
              data-testid={`billing-tab-${p.id}`}
            >
              <span className="truncate">{p.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
        {panels.map((p) => (
          <TabsContent
            key={p.id}
            value={p.id}
            // White card on the grey canvas (Dashboard-Ruhe, 2026-10-13). Not for `plans`: its PricingCards are
            // cards already — a frame around them would be card-in-card.
            className={
              p.id === 'plans'
                ? 'min-w-0 pt-4'
                : 'mt-4 min-w-0 rounded-xl border border-[var(--border-subtle)] bg-card p-4 sm:p-6'
            }
            data-testid={`billing-panel-${p.id}`}
          >
            {p.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  )
}
